<template>
  <div
    v-editable="blok"
    class="video-and-text"
    :class="[blok.flip_content && 'video-and-text--flipped']"
  >
    <div class="video-and-text__layout">
      <div class="video-and-text__content">
        <div class="video-and-text__content-text">
          <CustomRTE :blok="blok.text[0]" class="video-and-text__text" />
          <CTAGroup
            v-if="blok.links && blok.links.length > 0"
            :ctas="blok.links"
            class="video-and-text__links"
          />
        </div>
      </div>
      <div class="video-and-text__image">
        <iframe
          width="100%"
          height="380"
          :src="videoID"
          title="YouTube video player"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ImageAndVideoBlokProps } from '@/types';

// Props
const props = defineProps<{
  blok: ImageAndVideoBlokProps;
}>();

const videoID = computed(() => {
  if (props.blok.videoURL.startsWith('https://www.youtube.com/watch?v=')) {
    return (
      'https://www.youtube.com/embed/' +
      props.blok.videoURL.split('=')[1] +
      '?rel=0'
    );
  } else if (props.blok.videoURL.startsWith('https://youtu.be/')) {
    return (
      'https://www.youtube.com/embed/' +
      props.blok.videoURL.split('/')[3] +
      '?rel=0'
    );
  }
});
</script>

<style lang="scss">
.video-and-text {
  @include container;

  &__layout {
    column-gap: columns(2);
    display: grid;
  }

  &__content {
    margin-block-start: var(--spacing-s);
    width: 100%;

    .picture {
      display: none;
    }
  }

  &__links {
    margin-block-start: var(--spacing-s);
  }

  @media all and #{$mq-s-max} {
    .video-and-text__content {
      order: 2;
    }
  }
}

@media all and #{$mq-m-min} {
  .video-and-text {
    padding-inline-end: 0;
    padding-inline-start: 0;

    &__layout {
      align-items: center;
      grid-template-columns: columns(7) columns(12);
      margin-inline-start: columns(2);
    }

    &__content {
      margin-block-start: 0;
    }

    &--flipped {
      .video-and-text__content {
        order: 1;
      }

      .video-and-text__layout {
        grid-template-columns: columns(12) columns(7);
      }
    }
  }
}

@media all and #{$mq-l-min} {
  .video-and-text {
    &__content {
      position: relative;
    }

    &--has-thumbnail {
      --thumbnail-height: 320px;
      --thumbnail-width: 240px;

      padding-block-start: clamp(
        var(--spacing-xxl),
        22.5%,
        var(--thumbnail-height)
      );

      .video-and-text__content {
        display: grid;
        grid-template-rows: 120px 1fr 120px;
        height: 100%;

        .picture {
          align-self: flex-end;
          display: block;
          grid-row: 1;
          margin-inline-end: columns(3);
        }

        picture {
          max-height: var(--thumbnail-height);
          max-width: var(--thumbnail-width);
        }
      }

      .video-and-text__content-text {
        align-self: center;
        grid-row: 2;
        padding-block: var(--spacing-l);
      }
    }

    &--flipped {
      .video-and-text__content {
        .picture {
          margin-inline: columns(3) 0;
        }
      }
    }
  }
}
</style>
